export type RequestOptions = {
	method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
	headers?: ModelHeaders;
	body?: BodyInit | null | undefined;
	noErrorCatch?: boolean;
	silentError?: boolean;
	requestReportStore?: RequestTracer;
}

export type ListOptions = {
	filters?: Record<string, string | string[]>;
	sorts?: Record<string, SortWay>[];
	locales?: string | string[];
	headers?: ModelHeaders;
	requestReportStore?: RequestTracer;
}

export type QueryParamValue =
	| string
	| number
	| boolean
	| null
	| undefined
	| readonly (string | number | boolean)[];

export type QueryParams = Record<string, QueryParamValue>;

export type SaveOptions = {
	stringifyAttributes?: boolean;
	params?: QueryParams;
	silentError?: boolean;
}

export type Response = {
	data: unknown;
	status: number;
	headers: Headers;
}

export type Options = {
	baseUrl: string;
}

type RequestStartCallback = (connector: AbstractConnector, path: string, options: RequestOptions) => void | Promise<void>;
type ErrorCallback = (error: unknown, path: string) => void | Promise<void>;

export default abstract class AbstractConnector {
	connectorPath?: string;

	private _onRequestStartCallbacks: RequestStartCallback[] = [];
	private _onErrorCallbacks: ErrorCallback[] = [];

	constructor(options: Options) {
		this.connectorPath = options.baseUrl;
	}

	clear() {
		this._onRequestStartCallbacks = [];
		this._onErrorCallbacks = [];
		return this;
	}

	/**
	 * Enregistre un callback exécuté juste avant l'envoi de toute requête par ce connecteur.
	 * Retourne une fonction de désinscription pour retirer le callback.
	 */
	onRequestStart(callback: RequestStartCallback): () => void {
		this._onRequestStartCallbacks.push(callback);
		return () => {
			this._onRequestStartCallbacks = this._onRequestStartCallbacks
				.filter(cb => cb !== callback);
		};
	}

	/**
	 * Enregistre un callback exécuté quand une requête de ce connecteur échoue (status 4xx/5xx).
	 * Point d'injection pour brancher un reporter d'erreurs (Sentry, etc.)
	 * Retourne une fonction de désinscription.
	 */
	onError(callback: ErrorCallback): () => void {
		this._onErrorCallbacks.push(callback);
		return () => {
			this._onErrorCallbacks = this._onErrorCallbacks
				.filter(cb => cb !== callback);
		};
	}

	protected async _emitRequestStart(path: string, options: RequestOptions = {}): Promise<void> {
		const callbacks = [...this._onRequestStartCallbacks];
		for (const cb of callbacks) {
			await cb(this, path, options);
		}
	}

	protected async _emitError(error: unknown, path: string): Promise<void> {
		const callbacks = [...this._onErrorCallbacks];
		for (const cb of callbacks) {
			await cb(error, path);
		}
	}

	abstract fetch(path: string, options?: RequestOptions): Promise<Response>;

	abstract list(path: string, options?: ListOptions): Promise<Response>;

	abstract save(path: string, attributes: ModelAttributes, options?: RequestOptions, saveOptions?: SaveOptions): Promise<Response>;

	abstract destroy(path: string): Promise<Response>;
}
